<template>
  <div class="grid">
    <div class="col-12">
      <box type="primary">
        <template #header>
          <div class="box-header-title">
            <i class="pi pi-info-circle kpi-info-icon" v-tooltip="t('Distribuicao por dia dos chats que aguardam resposta e dos chats nao lidos')"></i>
            {{ t('Chats Sem Resposta por Dia') }}
          </div>
        </template>
        <charts
          v-if="lineSeries.length > 0"
          type="line"
          :series="lineSeries"
          :options="lineOptions"
          height="300px"
          :isLoading="isLoading"
        />
        <div v-else class="flex justify-content-center align-items-center" style="height: 300px;">
          <span class="text-color-secondary">{{ t('Nenhum chat sem resposta') }}</span>
        </div>
      </box>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface PorDiaPoint {
  data: string;
  chats_sem_resposta?: number;
  chats_nao_lidos?: number;
}

interface DeviceUnansweredData {
  device_name: string;
  por_dia?: PorDiaPoint[];
}

interface UnansweredDataShape {
  por_dispositivo?: DeviceUnansweredData[];
}

interface ChartSeries {
  name: string;
  data: number[];
  color: string;
}

const props = withDefaults(defineProps<{
  data?: UnansweredDataShape | null;
  isLoading?: boolean;
}>(), {
  data: null,
  isLoading: false,
});

const devices = computed<DeviceUnansweredData[]>(() => {
  if (!props.data || !props.data.por_dispositivo) return [];
  return props.data.por_dispositivo;
});

const categories = computed<string[]>(() => {
  // Collect all unique dates across all devices, sorted ascending
  const dateSet = new Set<string>();
  devices.value.forEach((device) => {
    (device.por_dia || []).forEach((d) => dateSet.add(d.data));
  });
  const sorted = [...dateSet].sort();
  return sorted.map((d) => {
    const parts = d.split('-');
    return `${parts[2]}/${parts[1]}`;
  });
});

const allDates = computed<string[]>(() => {
  const dates = new Set<string>();
  devices.value.forEach((device) => {
    (device.por_dia || []).forEach((d) => dates.add(d.data));
  });
  return [...dates].sort();
});

const deviceColors: string[] = ['#EF4444', '#F59E0B', '#8B5CF6', '#3B82F6', '#10B981', '#06B6D4', '#EC4899', '#84CC16'];

const lineSeries = computed<ChartSeries[]>(() => {
  if (devices.value.length === 0) return [];
  const series: ChartSeries[] = [];
  devices.value.forEach((device, i) => {
    const dailyMap: Record<string, PorDiaPoint> = {};
    (device.por_dia || []).forEach((d) => { dailyMap[d.data] = d; });
    const colorIdx = (i * 2) % deviceColors.length;

    series.push({
      name: `${device.device_name} - ${t('Sem Resposta')}`,
      data: allDates.value.map((date) => dailyMap[date] ? (dailyMap[date].chats_sem_resposta ?? 0) : 0),
      color: deviceColors[colorIdx]
    });
    series.push({
      name: `${device.device_name} - ${t('Não Lidos')}`,
      data: allDates.value.map((date) => dailyMap[date] ? (dailyMap[date].chats_nao_lidos ?? 0) : 0),
      color: deviceColors[(colorIdx + 1) % deviceColors.length]
    });
  });
  return series;
});

const lineOptions = computed(() => ({
  chart: {
    toolbar: { show: false }
  },
  xaxis: {
    categories: categories.value
  },
  yaxis: {
    title: { text: t('Chats') }
  },
  stroke: {
    curve: 'smooth',
    width: 2
  },
  legend: {
    position: 'top'
  }
}));
</script>

<style scoped>
.kpi-info-icon {
  font-size: 12px;
  color: #999;
  cursor: help;
  opacity: 0.7;
}

.kpi-info-icon:hover {
  opacity: 1;
  color: #666;
}
</style>
